import React from 'react';
import isNil from 'lodash/isNil';
import type { IssueId } from '../../../model/issue/issue-types';
import type { IssueProjectEntities } from '../../../model/software/software-types';
import { useBoardSelector } from '../../../state';
import { getIssueById } from '../../../state/selectors/issue/issue-selectors';
import {
	getIssueProjects,
	getIsIncrementPlanningBoard,
	getIncrementPlanningEstimateFieldId,
} from '../../../state/selectors/software/software-selectors';
import { getEstimationStatistic } from '../../../state/selectors/work/work-selectors';
import { FieldSyncAssignee } from './field-sync-assignee';
import { FieldSyncEstimate } from './field-sync-estimate';
import { FieldSyncParent } from './field-sync-parent';
import { FieldSyncSummary } from './field-sync-summary';

export type SyncSettings = {
	syncAssignee?: boolean;
	syncEstimate?: boolean;
	syncParent?: boolean;
	syncSummary?: boolean;
};

export type Props = SyncSettings & {
	issueId: IssueId;
};

export const FieldSync = ({
	issueId,
	syncAssignee = true,
	syncEstimate = true,
	syncParent = true,
	syncSummary = true,
}: Props) => {
	const issue = useBoardSelector((state) => getIssueById(state, issueId));
	const isIPBoard = useBoardSelector(getIsIncrementPlanningBoard);
	const estimationStatistic = useBoardSelector(getEstimationStatistic);
	const incrementPlanningEstimateFieldId = useBoardSelector(getIncrementPlanningEstimateFieldId);
	const estimateFieldId = isIPBoard ? incrementPlanningEstimateFieldId : estimationStatistic;

	const projects: IssueProjectEntities = useBoardSelector(getIssueProjects);
	const projectKey = issue && projects[issue.projectId]?.key;

	if (!issue) {
		return null;
	}

	return (
		<>
			{syncSummary && <FieldSyncSummary issue={issue} projectKey={projectKey} />}
			{syncParent && <FieldSyncParent issue={issue} projectKey={projectKey} />}
			{syncAssignee && <FieldSyncAssignee issue={issue} />}
			{syncEstimate && !isNil(estimateFieldId) && (
				<FieldSyncEstimate
					issue={issue}
					estimateFieldId={estimateFieldId}
					projectKey={projectKey}
				/>
			)}
		</>
	);
};
